<script setup>
/**
 * The 80px icon rail: the top-level areas of the redesigned shell.
 *
 * The rail is always icon-only. Its labels live in the tooltip and the
 * `aria-label`, and the menu panel beside it carries the section's own links.
 *
 * Pointing at an icon only shows its tooltip - it never swaps the panel's
 * contents, and selecting a section is still a click. Pointing anywhere at the
 * rail while the panel is pinned shut does peek it open over the content until
 * the pointer leaves (see `useSidebarMode`).
 *
 * Permanently dark in both themes via the `n-rail-*` tokens, which is why those
 * tokens exist rather than reusing a surface variable.
 *
 * Below the shell breakpoint the rail stays visible at a narrower width and a
 * section tap opens that section's menu as the off-canvas drawer instead of
 * navigating — the drawer needs the rail to switch sections on mobile.
 */
import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { isVoiceCallEnabled } from 'dashboard/helper/inbox';
import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';
import RailCreateMenu from './RailCreateMenu.vue';
import RailSupportButton from './RailSupportButton.vue';
import tolaLogo from 'dashboard/assets/images/tola-logo.svg';
import { RAIL_SECTIONS, findRailSectionForRoute } from './sections';
import { useSidebarMode } from './useSidebarMode';

const emit = defineEmits(['showCreateAccountModal', 'sectionTap']);

const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();

const brandLink = computed(() => accountScopedRoute('home'));

const activeSection = computed(() => {
  return findRailSectionForRoute(route.name)?.name;
});

// With the panel closed the rail is all that is left of the navigation, so the
// Inbox icon carries the same unread total the Conversations group shows. It is
// the only area with an unread notion today; the others resolve to 0 and render
// nothing.
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);

const RAIL_UNREAD_COUNTS = { inbox: () => allUnreadCount.value };

const unreadCountFor = section => {
  const count = Number(RAIL_UNREAD_COUNTS[section.name]?.());
  return Number.isFinite(count) && count > 0 ? count : 0;
};

// Calls only earns its rail slot once an inbox actually has calling switched
// on - with the feature flag alone the page behind it would sit empty.
const inboxes = useMapGetter('inboxes/getInboxes');

const RAIL_SECTION_CHECKS = {
  calls: () => inboxes.value.some(isVoiceCallEnabled),
};

const sections = computed(() =>
  RAIL_SECTIONS.filter(
    section =>
      shouldShow(
        section.featureFlag,
        section.permissions,
        section.installationTypes
      ) &&
      (RAIL_SECTION_CHECKS[section.name]?.() ?? true)
  ).map(section => {
    const label = t(section.labelKey);
    const unreadCount = unreadCountFor(section);

    return {
      ...section,
      label,
      unreadCount,
      // Same 99+ cap as the sidebar badges, so a busy workspace never widens
      // the badge past its icon tile.
      unreadLabel: unreadCount > 99 ? '99+' : String(unreadCount),
      // The destination has to stay in the name - "12 unread" alone would not
      // say where the link goes.
      ariaLabel: unreadCount
        ? t('RAIL.UNREAD_LABEL', { section: label, count: unreadCount })
        : label,
      to: accountScopedRoute(section.routeName, section.params),
      isActive: activeSection.value === section.name,
    };
  })
);

// Settings is pinned into the bottom cluster, text.com-style; the scrollable
// list carries the rest.
const listSections = computed(() =>
  sections.value.filter(section => !section.pinToBottom)
);
const pinnedSections = computed(() =>
  sections.value.filter(section => section.pinToBottom)
);

const { isDesktop, openPeek, closePeek, resetPeek } = useSidebarMode();

// A pointer that leaves the window never fires `mouseleave` on the rail, so the
// peek would hang open behind an alt-tab.
const dismissPeek = () => closePeek(0);

onMounted(() => {
  window.addEventListener('blur', dismissPeek);
  document.addEventListener('mouseleave', dismissPeek);
});

onUnmounted(() => {
  window.removeEventListener('blur', dismissPeek);
  document.removeEventListener('mouseleave', dismissPeek);
  resetPeek();
});

// On mobile the tap must not navigate: the shell previews the section's menu
// in the drawer first, and only a leaf link commits to a route. The links
// render through RouterLink's `custom` slot because the built-in click handler
// navigates before an added listener could prevent it.
const onSectionClick = (section, event, navigate) => {
  if (isDesktop.value) {
    navigate(event);
    return;
  }
  event.preventDefault();
  emit('sectionTap', { name: section.name, to: section.to });
};
</script>

<template>
  <nav
    id="product-rail"
    :aria-label="t('RAIL.LABEL')"
    class="relative z-40 flex w-12 flex-shrink-0 flex-col items-center bg-n-rail shell:w-20"
    @mouseenter="openPeek"
    @mouseleave="closePeek()"
  >
    <RouterLink
      :to="brandLink"
      :aria-label="t('RAIL.BRAND')"
      class="grid size-12 flex-shrink-0 place-items-center outline outline-1 outline-transparent focus-visible:outline-2 -outline-offset-2 focus-visible:outline-n-rail-indicator shell:size-20"
    >
      <img :src="tolaLogo" alt="" class="size-7 shell:size-9" />
    </RouterLink>

    <div
      class="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto pb-2 no-scrollbar"
    >
      <RouterLink
        v-for="section in listSections"
        :key="section.name"
        v-slot="{ href, navigate }"
        :to="section.to"
        custom
      >
        <a
          v-tooltip.right="section.label"
          :href="href"
          :aria-label="section.ariaLabel"
          :aria-current="section.isActive ? 'page' : undefined"
          class="relative grid size-10 flex-shrink-0 place-items-center rounded-[10px] transition-colors duration-200 ease-out outline outline-1 outline-transparent focus-visible:outline-2 focus-visible:outline-n-rail-indicator active:scale-[0.98] motion-reduce:transition-none shell:size-[46px]"
          :class="
            section.isActive
              ? 'bg-n-rail-active text-n-rail-text-active'
              : 'text-n-rail-text hover:bg-n-rail-hover hover:text-n-rail-text-active'
          "
          @click="onSectionClick(section, $event, navigate)"
        >
          <span
            :class="section.isActive ? section.activeIcon : section.icon"
            class="size-5 flex-shrink-0"
          />
          <!-- The ring is the rail's own colour, so the badge reads as sitting
               on top of the icon rather than merging with it. -->
          <span
            v-if="section.unreadCount"
            :data-test-id="`rail-unread-badge-${section.name}`"
            aria-hidden="true"
            class="absolute top-1 ltr:right-1 rtl:left-1 inline-grid h-4 min-w-4 place-items-center rounded-full bg-n-ruby-11 px-1 text-[10px] font-semibold leading-none tabular-nums text-white ring-2 ring-n-rail dark:bg-n-ruby-8"
          >
            {{ section.unreadLabel }}
          </span>
        </a>
      </RouterLink>
    </div>

    <div
      class="flex flex-shrink-0 flex-col items-center gap-1 border-t border-n-rail-hover py-3"
    >
      <RailCreateMenu />

      <RailSupportButton />

      <RouterLink
        v-for="section in pinnedSections"
        :key="section.name"
        v-slot="{ href, navigate }"
        :to="section.to"
        custom
      >
        <a
          v-tooltip.right="section.label"
          :href="href"
          :aria-label="section.label"
          :aria-current="section.isActive ? 'page' : undefined"
          class="grid size-10 flex-shrink-0 place-items-center rounded-[10px] transition-colors duration-200 ease-out outline outline-1 outline-transparent focus-visible:outline-2 focus-visible:outline-n-rail-indicator active:scale-[0.98] motion-reduce:transition-none shell:size-[46px]"
          :class="
            section.isActive
              ? 'bg-n-rail-active text-n-rail-text-active'
              : 'text-n-rail-text hover:bg-n-rail-hover hover:text-n-rail-text-active'
          "
          @click="onSectionClick(section, $event, navigate)"
        >
          <span
            :class="section.isActive ? section.activeIcon : section.icon"
            class="size-5 flex-shrink-0"
          />
        </a>
      </RouterLink>

      <SidebarProfileMenu
        @show-create-account-modal="emit('showCreateAccountModal')"
      />
    </div>
  </nav>
</template>
